import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import RobokassaService from '../../services/RobokassaService';

const PaymentSuccess: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState<{
    amount: string;
    orderId: string;
    invoiceId: string;
  } | null>(null);

  useEffect(() => {
    verifyPayment();
  }, []);

  const verifyPayment = async () => {
    try {
      // Проверяем, есть ли параметры от Robokassa
      const callbackParams = RobokassaService.parseCallbackUrl();

      if (callbackParams) {
        // Это возврат от Robokassa - проверяем подпись
        const { OutSum, InvId, SignatureValue } = callbackParams;

        // В реальном приложении здесь нужно отправить запрос на бэкенд
        // для проверки подписи и получения статуса платежа
        // Для сейчас просто показываем успешную оплату

        setPaymentInfo({
          amount: OutSum,
          orderId: InvId,
          invoiceId: InvId,
        });
        setVerified(true);
      } else {
        // Обычный переход на success страницу (без параметров)
        setVerified(true);
      }
    } catch (error) {
      console.error('Payment verification error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoToStore = () => {
    navigate('/fsm/pricing');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleViewOrders = () => {
    navigate('/orders');
  };

  const handleViewTransactions = () => {
    navigate('/financial/transactions');
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="card">
              <div className="card-body text-center">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">{t('loading')}</span>
                </div>
                <h5>{t('financial.verifyingPayment')}</h5>
                <p className="text-muted">{t('financial.pleaseWait')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="col-md-8">
          <div className="card border-success">
            <div className="card-body text-center">
              <div className="mb-4">
                <i
                  className="fas fa-check-circle text-success"
                  style={{ fontSize: 80 }}
                />
              </div>

              <h2 className="mb-3 text-success">
                {t('financial.paymentSuccess')}
              </h2>

              <p className="lead mb-4">
                {t('financial.paymentSuccessDescription')}
              </p>

              {paymentInfo && (
                <div className="alert alert-success">
                  <h5 className="alert-heading">{t('financial.paymentDetails')}</h5>
                  <hr />
                  <div className="row text-start">
                    <div className="col-6 mb-2">
                      <strong>{t('financial.amount')}:</strong>
                    </div>
                    <div className="col-6 mb-2 text-end">
                      {paymentInfo.amount} ₽
                    </div>

                    <div className="col-6 mb-2">
                      <strong>{t('financial.orderId')}:</strong>
                    </div>
                    <div className="col-6 mb-2 text-end">
                      #{paymentInfo.orderId}
                    </div>

                    <div className="col-6 mb-2">
                      <strong>{t('financial.invoiceId')}:</strong>
                    </div>
                    <div className="col-6 mb-2 text-end">
                      {paymentInfo.invoiceId}
                    </div>
                  </div>
                </div>
              )}

              <div className="alert alert-info">
                <i className="fas fa-info-circle me-2" />
                {t('financial.paymentProcessedMessage')}
              </div>

              <div className="d-flex gap-2 justify-content-center mt-4">
                <button
                  className="btn btn-success btn-lg"
                  onClick={handleGoToStore}
                >
                  <i className="fas fa-store me-2" />
                  {t('financial.backToStore')}
                </button>
              </div>

              <div className="d-flex gap-2 justify-content-center mt-3">
                <button
                  className="btn btn-outline-secondary"
                  onClick={handleGoHome}
                >
                  <i className="fas fa-home me-2" />
                  {t('home')}
                </button>

                <button
                  className="btn btn-outline-primary"
                  onClick={handleViewOrders}
                >
                  <i className="fas fa-list me-2" />
                  {t('myOrders')}
                </button>

                <button
                  className="btn btn-primary"
                  onClick={handleViewTransactions}
                >
                  <i className="fas fa-receipt me-2" />
                  {t('transactions')}
                </button>
              </div>
            </div>
          </div>

          {/* Дополнительная информация */}
          <div className="card mt-4">
            <div className="card-body">
              <h5 className="card-title">
                <i className="fas fa-question-circle me-2" />
                {t('financial.whatNext')}
              </h5>
              <ul className="mb-0">
                <li>{t('financial.nextStep1')}</li>
                <li>{t('financial.nextStep2')}</li>
                <li>{t('financial.nextStep3')}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
